/**
 * @module IDEE/layer/GeoJSON
 */
import GeoJSONImpl from 'impl/layer/GeoJSON';
import LayerVector from './Vector';
import { GeoJSON as GeoJSONType } from './Type';
import {
  isUndefined, isArray, isNullOrEmpty, isString, isObject,
} from '../util/Utils';
import Exception from '../exception/exception';
import { getValue } from '../i18n/language';

/**
 * @classdesc
 * GeoJSON, a pesar de no ser un estándar OGC (está en camino de convertirse en uno),
 * es un formato de intercambio de información geográfica muy extendido que, al igual que WFS,
 * permite que todos los elementos estén en el cliente.
 *
 * @property {String} idLayer Identificador de la capa.
 * @property {String} url Url del archivo o servicio que genera el GeoJSON.
 * @property {String} name Nombre de la capa, identificador.
 * @property {Object} source Fuente GeoJSON.
 * @property {Boolean} extract Activa la consulta al hacer clic sobre un objeto geográfico,
 * por defecto verdadero.
 * @property {Boolean} transparent (deprecated) Falso si es una capa base,
 * verdadero en caso contrario.
 * @property {Boolean} isBase Define si la capa es base.
 * @property {String} template Plantilla que se mostrará al consultar un objeto geográfico.
 * @property {Object} options Opciones GeoJSON.
 * @param {Object} vendorOptions Opciones para la biblioteca base. Ejemplo vendorOptions:
 * <pre><code>
 * import OLSourceVector from 'ol/source/Vector';
 * {
 *  opacity: 0.1,
 *  source: new OLSourceVector({
 *    attributions: 'geojson',
 *    ...
 *  })
 * }
 * </code></pre>
 * @api
 * @extends {IDEE.layer.Vector}
 */
class GeoJSON extends LayerVector {
  /**
   * Constructor principal de la clase. Crea una capa GeoJSON
   * con parámetros especificados por el usuario.
   *
   * @constructor
   * @param {String|Mx.parameters.GeoJSON} parameters Parámetros para la construcción de la capa,
   * estos parámetros los proporciona el usuario.
   * - name: Nombre de la capa en la leyenda.
   * - url: Url del fichero o servicio que genera el GeoJSON.
   * - extract: Opcional, activa la consulta por click en el objeto geográfico,
   * por defecto verdadero.
   * - source: Fuente de la capa.
   * - type: Tipo de la capa.
   * - maxExtent: La medida en que restringe la visualización a una región específica.
   * - legend: Indica el nombre que queremos que aparezca en el árbol de contenidos, si lo hay.
   * - isBase: Indica si la capa es base.
   * - transparent (deprecated): Falso si es una capa base, verdadero en caso contrario.
   * - template: (opcional) Plantilla que se mostrará al consultar un objeto geográfico.
   * @param {Mx.parameters.LayerOptions} options Estas opciones se mandarán a la implementación.
   * - hide: Atributos ocultos.
   * - show: Mostrar atributos.
   * - minZoom: Zoom mínimo aplicable a la capa.
   * - maxZoom: Zoom máximo aplicable a la capa.
   * - minScale: Escala mínima.
   * - maxScale: Escala máxima.
   * - visibility: Define si la capa es visible o no. Verdadero por defecto.
   * - displayInLayerSwitcher: Indica si la capa se muestra en el selector de capas.
   * - opacity: Opacidad de capa, por defecto 1.
   * - style: Define el estilo de la capa.
   * - predefinedStyles: Estilos predefinidos para la capa.
   * - height: Define la altura del objeto geográfico. Puede ser un número o una propiedad.
   *   Si se define la altura será constante para todos los puntos del objeto geográfico.
   *   Solo disponible para Cesium.
   * - clampToGround: Define si el objeto geográfico se debe ajustar al suelo. Si las coordenadas
   *   son 3D, por defecto es falso, en caso contrario es verdadero.
   *   Solo disponible para Cesium.
   * @api
   */
  constructor(parameters = {}, options = {}, vendorOptions = {}) {
    // Comprueba si la implementación puede crear capas GeoJSON
    if (isUndefined(GeoJSONImpl) || (isObject(GeoJSONImpl)
      && isNullOrEmpty(Object.keys(GeoJSONImpl)))) {
      Exception(getValue('exception').geojsonlayer_method);
    }

    // Comprueba si los parámetros son null or empty
    if (isNullOrEmpty(parameters)) {
      Exception(getValue('exception').no_param);
    }

    if (!isUndefined(parameters.transparent)) {
      // eslint-disable-next-line no-console
      console.warn(getValue('exception').transparent_deprecated);
    }

    const optionsVar = options;

    if (typeof parameters !== 'string') {
      optionsVar.maxExtent = parameters.maxExtent;
    }

    /**
     * Implementación
     * @public
     * @implements {IDEE.impl.layer.GeoJSON}
     * @type {IDEE.impl.layer.GeoJSON}
     */
    const impl = new GeoJSONImpl(parameters, optionsVar, vendorOptions);

    const opts = parameters;
    opts.type = GeoJSONType;

    // Llama al contructor del que se extiende la clase
    super(opts, optionsVar, undefined, impl);
    this.constructorParameters = { parameters, options, vendorOptions };

    if (isString(parameters)) {
      this.url = parameters;
    } else if (isArray(parameters)) {
      this.source = parameters;
    } else {
      /**
       * GeoJSON url: Url del archivo o servicio que genera el GeoJSON.
       */
      this.url = parameters.url;

      /**
       * GeoJSON name: Nombre de la capa.
       */
      this.name = parameters.name;

      /**
       * GeoJSON Fuente de la capa.
       */
      this.source = parameters.source;
      if (isString(this.source)) {
        this.source = this.deserialize(this.source);
      }

      /**
       * GeoJSON crs: Sistema de Referencia de Coordenadas.
       * @public
       * @type {Object}
       * @api
       */
      if (!isNullOrEmpty(parameters.crs)) {
        if (isNullOrEmpty(this.source)) {
          this.source = {
            type: 'FeatureCollection',
            features: [],
          };
        }
        this.source.crs = {
          type: 'EPSG',
          properties: {
            code: parameters.crs,
          },
        };
      }
    }

    /**
     * GeoJSON minZoom: Límite del zoom mínimo.
     * @public
     * @type {Number}
     */
    this.minZoom = optionsVar.minZoom || Number.NEGATIVE_INFINITY;

    /**
     * GeoJSON maxZoom: Límite del zoom máximo.
     * @public
     * @type {Number}
     */
    this.maxZoom = optionsVar.maxZoom || Number.POSITIVE_INFINITY;

    /**
     * GeoJSON options: Opciones que se mandan a la implementación.
     */
    this.options = options;
  }

  /**
   * Devuelve la fuente del GeoJSON.
   *
   * @function
   * @getter
   * @return {Object} Fuente del GeoJSON.
   * @api
   */
  get source() {
    return this.getImpl().source;
  }

  /**
   * Sobrescribe la fuente de la capa.
   *
   * @function
   * @setter
   * @param {Object} newSource Nueva fuente para la capa de tipo GeoJSON.
   * @api
   */
  set source(newSource) {
    this.getImpl().source = newSource;
  }

  /**
   * Este método comprueba si un objeto es igual
   * a esta capa.
   *
   * @function
   * @param {Object} obj Objeto a comparar.
   * @returns {Boolean} Valor verdadero es igual, falso no lo es.
   * @api
   */
  equals(obj) {
    let equals = false;

    if (obj instanceof GeoJSON) {
      equals = this.name === obj.name;
      equals = equals && (this.extract === obj.extract);
      equals = equals && (this.idLayer === obj.idLayer);
      equals = equals && (this.template === obj.template);
    }

    return equals;
  }

  /**
   * Sobreescribe la URL de la capa.
   *
   * @function
   * @public
   * @param {String} newURL URL
   * @api
   */
  setURL(newURL) {
    this.getImpl().setURL(newURL);
  }

  /**
   * Este método sobrescribe la fuente del GeoJSON.
   *
   * @function
   * @param {Object} source Nueva fuente.
   * @api
   */
  setSource(source) {
    // eslint-disable-next-line no-underscore-dangle
    this.getImpl().loadFeaturesPromise_ = null;
    this.source = source;
    this.getImpl().refresh(source);
  }

  /**
   * Este método establece el estilo en capa.
   *
   * @function
   * @public
   * @param {IDEE.Style|String} styleParam Estilo que se aplicará a la capa.
   * @param {bool} applyToFeature Si el valor es verdadero se aplicará a los objetos geográficos,
   * falso no.
   * Por defecto, falso.
   * @param {IDEE.layer.GeoJSON.DEFAULT_OPTIONS_STYLE} defaultStyle Estilo por defecto,
   * se define en GeoJSON.js.
   * @api
   */
  setStyle(styleParam, applyToFeature = false, defaultStyle = GeoJSON.DEFAULT_OPTIONS_STYLE) {
    super.setStyle(styleParam, applyToFeature, defaultStyle);
  }

  /**
   * Este método serializa la fuente del GeoJSON.
   *
   * @function
   * @return {String} Devuelve la fuente serializada, utiliza "encodeURIComponent".
   * @api
   * @public
   */
  serialize() {
    return window.btoa(unescape(encodeURIComponent(JSON.stringify(this.source))));
  }

  /**
   * Este método deserializa la fuente, utiliza "decodeURIComponent".
   *
   * @function
   * @param {String} encodedSerialized Fuente serializada.
   * @return {String} Devuelve la fuente deserializada.
   * @api
   * @public
   */
  deserialize(encodedSerialized) {
    const source = JSON.parse(decodeURIComponent(escape(window.atob(encodedSerialized.replace(' ', '+')))));
    return source;
  }
}

/**
 * Estilos por defecto del GeoJSON.
 * @type {Object}
 * @public
 * @api
 */
GeoJSON.DEFAULT_PARAMS = {
  fill: {
    color: 'rgba(255, 255, 255, 0.4)',
    opacity: 0.4,
  },
  stroke: {
    color: '#3399CC',
    width: 1.5,
  },
};

/**
 * Parámetros por defecto del GeoJSON.
 * @type {Object}
 * @public
 * @api
 */
GeoJSON.DEFAULT_OPTIONS_STYLE = {
  point: {
    ...GeoJSON.DEFAULT_PARAMS,
    radius: 5,
  },
  line: {
    ...GeoJSON.DEFAULT_PARAMS,
  },
  polygon: {
    ...GeoJSON.DEFAULT_PARAMS,
  },
};

export default GeoJSON;
